// Operator colours → CSS custom properties. The stylesheet (styles/app.css) defines every token with
// the product's own palette; this only overrides the brand-driven ones, per theme, after checking
// contrast so an operator can pick any colour without making text unreadable.
import { ensureContrast, filledColor, hueShift, normalizeHex, rgba, shadeFor } from "./color";

export type ThemeName = "light" | "dark";

/** Backgrounds brand-coloured text and marks sit on, per theme (mirrors app.css). */
export const THEME_BACKGROUNDS: Record<ThemeName, string[]> = {
  light: ["#ffffff", "#f4f5f9", "#eef0f6", "#f7f8fb"],
  dark: ["#07080c", "#0f1016", "#14151c", "#191b23"],
};

/** WCAG AA for normal text, and for non-text UI (focus rings, status marks). */
export const TEXT_CONTRAST = 4.5;
export const UI_CONTRAST = 3;

export function brandTokens(primaryColor: string, accentColor: string, theme: ThemeName): Record<string, string> {
  const primary = normalizeHex(primaryColor);
  const accent = normalizeHex(accentColor);
  if (!primary || !accent) throw new Error("brand colours must be #RRGGBB");
  const bgs = THEME_BACKGROUNDS[theme];
  const dark = theme === "dark";
  // Filled buttons: the brand colour itself (the gradient and glow keep it lifted on dark grounds).
  const fill = filledColor(primary, TEXT_CONTRAST);
  // Links, focus rings and highlighted figures use the primary as text: it must read on every ground.
  const text = ensureContrast(primary, bgs, TEXT_CONTRAST);
  const mark = ensureContrast(accent, bgs, UI_CONTRAST);
  // Second stop of the primary gradient: the brand hue turned slightly toward blue, shaded so the
  // button text keeps AA contrast across the whole gradient.
  const fill2 = shadeFor(hueShift(fill.bg, -22), fill.fg);
  return {
    "--accent": fill.bg,
    "--accent-2": fill2,
    "--glow-1": rgba(primary, dark ? 0.3 : 0.16),
    "--glow-2": rgba(accent, dark ? 0.16 : 0.12),
    "--accent-hover": fill.hover,
    "--accent-press": fill.press,
    "--on-accent": fill.fg,
    "--accent-text": text,
    "--accent-soft": rgba(text, dark ? 0.14 : 0.08),
    "--accent-ring": rgba(text, dark ? 0.45 : 0.32),
    "--mark": rgba(text, dark ? 0.22 : 0.12),
    "--info-soft": rgba(text, dark ? 0.08 : 0.05),
    "--info-line": rgba(text, dark ? 0.24 : 0.18),
    "--brand-accent": mark,
    "--brand-accent-soft": rgba(mark, dark ? 0.16 : 0.14),
  };
}

const SAFE_CSS = /^[\w\s\-:;#.,()%"=[\]{}]*$/;

/** A stylesheet overriding the brand tokens for both themes. Only hex/rgba values ever go in. */
export function brandCss(primaryColor: string, accentColor: string): string {
  const block = (theme: ThemeName) => {
    const decls = Object.entries(brandTokens(primaryColor, accentColor, theme))
      .map(([k, v]) => `${k}:${v}`)
      .join(";");
    // `html:root[...]` outranks app.css's `:root[...]` regardless of stylesheet order.
    return `html:root[data-theme="${theme}"]{${decls}}`;
  };
  const css = `${block("light")}\n${block("dark")}`;
  if (!SAFE_CSS.test(css)) throw new Error("unexpected characters in brand CSS");
  return css;
}

export function isSafeBrandCss(css: unknown): css is string {
  return typeof css === "string" && css.length < 4000 && SAFE_CSS.test(css);
}

export const BRAND_STYLE_ID = "mc-brand";

/** Install (or with `null`, remove) the brand stylesheet. */
export function applyBrandCss(css: string | null, doc: Document = document): void {
  const existing = doc.getElementById(BRAND_STYLE_ID);
  if (!css) {
    existing?.remove();
    return;
  }
  if (!isSafeBrandCss(css)) return;
  const el = existing ?? doc.head.appendChild(Object.assign(doc.createElement("style"), { id: BRAND_STYLE_ID }));
  if (el.textContent !== css) el.textContent = css;
}
